import { useEffect, useState, useCallback } from "react";
import api, { formatErr } from "../lib/api";
import { useEventSocket } from "../lib/ws";
import { useMotif } from "../contexts/MotifContext";
import { toast } from "sonner";
import { Camera, Check, Crown, X, Pause, Play, ArrowRight, ChevronUp } from "lucide-react";
import { T } from "../lib/i18n";

export default function PhotoQueuePanel() {
  const { eventId } = useMotif();
  const [queue, setQueue] = useState([]);
  const [paused, setPaused] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/queue");
      setQueue(data);
      const { data: ev } = await api.get("/events/current");
      setPaused(!!ev.queue_paused);
    } catch (e) { toast.error(formatErr(e)); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEventSocket(eventId, (msg) => {
    if (msg.type === "queue_update" || msg.type === "ws_reconnected") refresh();
  });

  const setStatus = async (id, status) => {
    try { await api.put(`/queue/${id}/status`, { status }); refresh(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const setPriority = async (id, level) => {
    try { await api.put(`/queue/${id}/priority`, { priority_level: level }); refresh(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const togglePause = async () => {
    try { await api.post("/queue/pause", { paused: !paused }); refresh(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const waiting = queue.filter((q) => q.status === "waiting" || q.status === "up_next");
  const inProgress = queue.find((q) => q.status === "in_progress");
  const done = queue.filter((q) => q.status === "done").length;

  return (
    <div className="card-elev p-5">
      <div className="flex items-center gap-2 mb-3">
        <Camera className="w-4 h-4 text-primary-motif" />
        <div className="font-display text-xl">{T("Photo queue")}</div>
        <span className="text-[10px] tracking-luxe text-motif-muted ml-2">
          {waiting.length} waiting · {done} done
        </span>
        <button
          onClick={togglePause}
          className="ml-auto btn-ghost text-xs flex items-center gap-1.5"
          data-testid="queue-pause-toggle"
        >
          {paused ? <><Play className="w-3 h-3" />{T("Resume")}</> : <><Pause className="w-3 h-3" />{T("Pause")}</>}
        </button>
      </div>

      {paused && (
        <div className="text-xs text-motif-muted bg-accent-motif p-2 rounded-lg mb-3">
          Queue paused — guests can still join, but they're told it's paused.
        </div>
      )}

      {inProgress && (
        <div className="p-3 rounded-xl mb-3 border-2" style={{ borderColor: "var(--motif-primary)", background: "var(--motif-accent)" }} data-testid={`queue-current-${inProgress.id}`}>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-luxe text-primary-motif">{T("now taking photos")}</div>
              <div className="font-display text-2xl text-motif">{inProgress.guest_name}</div>
              <div className="text-xs text-motif-muted">Table {inProgress.table_name}</div>
            </div>
            <button onClick={() => setStatus(inProgress.id, "done")} className="btn-primary text-xs px-3 py-2 flex items-center gap-1.5" data-testid={`queue-done-${inProgress.id}`}>
              <Check className="w-3.5 h-3.5" />Done
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2 max-h-[50vh] overflow-y-auto">
        {waiting.map((q, i) => {
          const isVip = (q.priority_level || 0) > 0;
          return (
            <div key={q.id} className="flex items-center gap-2 p-2 rounded-xl bg-accent-motif" data-testid={`queue-row-${q.id}`}>
              <div className="font-display text-xl text-primary-motif w-6 text-center">{i + 1}</div>
              <div className="flex-1">
                <div className="text-sm font-medium">{q.guest_name} {isVip && <Crown className="w-3 h-3 inline text-primary-motif" />}</div>
                <div className="text-[10px] text-motif-muted">Table {q.table_name}</div>
              </div>
              <button
                onClick={() => setPriority(q.id, isVip ? 0 : 1)}
                className="btn-ghost text-xs p-1.5"
                title={isVip ? "Remove VIP" : "Mark VIP"}
                data-testid={`queue-vip-${q.id}`}
              >
                <Crown className="w-3 h-3" />
              </button>
              {i === 0 && (
                <button onClick={() => setStatus(q.id, "in_progress")} className="btn-primary text-xs px-2 py-1.5 flex items-center gap-1" data-testid={`queue-start-${q.id}`}>
                  <ArrowRight className="w-3 h-3" />Start
                </button>
              )}
              <button onClick={() => setStatus(q.id, "skipped")} className="btn-ghost text-xs p-1.5" title="Skip" data-testid={`queue-skip-${q.id}`}>
                <X className="w-3 h-3" />
              </button>
            </div>
          );
        })}
        {waiting.length === 0 && !inProgress && (
          <div className="text-motif-muted text-sm text-center py-6">{T("No one waiting.")}</div>
        )}
      </div>
    </div>
  );
}
